import { Card } from "@/components/ui/card";
import { useEffectiveUser } from "@/hooks/useEffectiveUser";
import { formatBigNumber, toNumber } from "@/lib/utils";
import { useStatsOverview } from "@/hooks/useStatsOverview";
import CustomLoadingError from "../ui/CustomLoadingError";
import { useState } from "react";
import { useEffect } from "react";
import { LoaderCircle } from "lucide-react";
import { useAppStore } from "@/store/app";

interface OverviewCardsProps {
  // for URL param: can be undefined, but not null
  exerciseNanoId?: string;
}

export function OverviewCards({ exerciseNanoId }: OverviewCardsProps) {
  const { user } = useEffectiveUser();

  const selectedRange = useAppStore((s) => s.selectedDaysRange);

  const { data, isLoading, isError } = useStatsOverview(
    Number(selectedRange),
    exerciseNanoId
  );

  // Show loading state for short time when selected range changes
  const [fakeLoading, setFakeLoading] = useState(false);
  useEffect(() => {
    setFakeLoading(true);
    const timer = setTimeout(() => {
      setFakeLoading(false);
    }, 200);

    return () => clearTimeout(timer);
  }, [selectedRange]);

  if (isError) return <CustomLoadingError />;

  // Convert the stats data into the format needed for display
  const stats = [
    {
      value: data?.overview.workouts_count,
      label: "WORKOUTS",
      showUnit: null,
    },
    {
      value: formatBigNumber(
        user?.preferred_weight_unit == "lb"
          ? data?.overview.volume_total_lb
          : data?.overview.volume_total_kg
      ),
      label: "TOTAL VOLUME",
      showUnit: user?.preferred_weight_unit,
    },

    {
      value: formatBigNumber(
        user?.preferred_weight_unit == "lb"
          ? data?.overview.volume_peak_lb
          : data?.overview.volume_peak_kg
      ),
      label: "PEAK DAILY VOLUME",
      showUnit: user?.preferred_weight_unit,
    },
    {
      value: formatBigNumber(
        user?.preferred_weight_unit == "lb"
          ? data?.overview.avg_daily_volume_lb
          : data?.overview.avg_daily_volume_kg
      ),
      label: "AVG DAILY VOLUME",
      showUnit: user?.preferred_weight_unit,
    },
    {
      value: data?.overview.active_days,
      label: "ACTIVE DAYS",
      showUnit: null,
    },
    {
      value: data?.overview.max_training_gap,
      label: "MAX TRAINING GAP",
      showUnit: "days",
    },
  ];

  // if (exerciseNanoId) {
  //   // push to the first position
  //   stats.unshift({
  //     value: formatBigNumber(
  //       user?.preferred_weight_unit == "lb"
  //         ? data?.overview.max_weight_lb
  //         : data?.overview.max_weight_kg
  //     ),
  //     label: "MAX WEIGHT TRACKED",
  //     showUnit: true,
  //   });
  // }

  return (
    <div className="grid grid-cols-2 gap-3">
      {stats.map((stat, index) => (
        <Card
          key={index}
          className="flex flex-col items-center justify-center gap-2 py-4 bg-white"
        >
          <div className="text-3xl font-bold text-tx-dark h-8">
            {fakeLoading || isLoading ? (
              <p className="text-accent-bg-cold/30">
                <LoaderCircle className="animate-spin w-6 h-6" />
              </p>
            ) : (
              <>
                <span>{stat.value}</span>

                {stat.showUnit && (
                  <span className="text-sm text-tx-muted font-medium ml-1">
                    {stat.showUnit}
                  </span>
                )}
              </>
            )}
          </div>
          <div className="text-xs text-tx-default uppercase tracking-wider">
            {stat.label}
          </div>
        </Card>
      ))}
    </div>
  );
}
